import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";

interface Title {
  id: number;
  name: string;
  name_trans?: {
    ar: string;
    en: string;
  };
}

interface TitlesResponse {
  data: Title[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useTitles = () => {
  return useQuery({
    queryKey: ["titles"],
    queryFn: async (): Promise<Title[]> => {
      const response = await USERAXIOS.get<TitlesResponse>("/titles?page=1");
      return response.data.data;
    },
  });
};

export const useTitlesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["titles", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<TitlesResponse> => {
      const response = await USERAXIOS.get<TitlesResponse>(`/titles`, {
        params: {
          page: pageParam,
          per_page: pageSize,
          title: searchTerm || undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const titles = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: titles,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
